import { useState } from "react";
import "styles/add-family.scss";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate, Outlet } from "react-router-dom";
import FamilyService from "services/FamilyService.js";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import useAPI from "hooks/useAPI.js";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";

export function Family(props) {
  return <Outlet />;
}

export function FamilyMembers() {
  let navigate = useNavigate();
  const theme = useTheme();
  const [familyMembers, setFamilyMembers] = useState([]);

  const getMembersAPI = useAPI(
    FamilyService.getMembers,
    getMembersHandler,
    false,
    true
  );

  function getMembersHandler(response) {
    setFamilyMembers(response.data || []);
  }

  return (
    <>
      <div className="ms-4">
        <Typography variant="h1" color={theme.palette.text.secondary}>
          My Family Members
        </Typography>
        <Typography
          variant="body1"
          color={theme.palette.text.main}
          className="mt-2"
        >
          Add patient (<b>self or family member</b>) profile under this section
          to register at the centre
        </Typography>
      </div>
      <div>
        {getMembersAPI.status === "pending" ? (
          <div className="text-center">
            <CircularProgress
              className="text-center"
              sx={{
                mt: 50,
              }}
            />
          </div>
        ) : familyMembers.length === 0 ? (
          <div className="family-empty-state">
            <Typography variant="h5">No family members added yet</Typography>
            <Typography variant="body2" color="text.secondary" className="mt-2">
              Add yourself or a family member to start scheduling, intake, and document workflows.
            </Typography>
            <div className="mt-3">
              <button
                type="button"
                className="family-primary-action"
                onClick={() => navigate("add-member")}
              >
                Add Family Member
              </button>
            </div>
          </div>
        ) : (
          <div className="family-container">
            <Grid container spacing={3}>
              {familyMembers.map((member) => (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={member.id}>
                  <FamilyMemberCard member={member} />
                </Grid>
              ))}
              <Grid item xs={12} sm={6} lg={4} xl={3}>
                <AddFamilyMemberCard clickHandler={() => navigate("add-member")} />
              </Grid>
            </Grid>
          </div>
        )}
      </div>
    </>
  );
}

function AddFamilyMemberCard({ clickHandler, name }) {
  return (
    <Paper elevation={0} className="family-tile family-add-tile" onClick={clickHandler}>
      <div className="family-add-content">
        <PersonAddAlt1RoundedIcon fontSize="large" color="primary" />
        <span className="family-add-title">Add Family Member</span>
        <span className="family-add-subtitle">Create a new profile for intake and care management</span>
      </div>
    </Paper>
  );
}

function FamilyMemberCard({ member }) {
  let navigate = useNavigate();
  const initials = `${member.firstName?.[0] || ""}${member.lastName?.[0] || ""}`.toUpperCase() || "P";

  return (
    <Paper elevation={0} className="family-tile family-member-tile">
      {member.isArchived ? (
        <h6 className="family-card-overlay-text">
          Archived
          <Tooltip title="This patient profile has been archived. Contact the centre for more information.">
            <InfoOutlinedIcon fontSize="inherit" color="custom.light" />
          </Tooltip>
        </h6>
      ) : null}
      <Stack spacing={2} className="family-tile-content">
        <Box className="family-tile-header">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar className="family-avatar">{initials}</Avatar>
            <div>
              <Typography variant="subtitle1" className="family-member-name">
                {`${member.firstName} ${member.lastName}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                DOB: {member.dob || "Not available"}
              </Typography>
            </div>
          </Stack>
        </Box>

        {!member.isArchived ? (
          <Stack direction="row" spacing={1} className="family-actions-row">
            <Tooltip title="Edit family member">
              <IconButton
                aria-label="Edit family member"
                className="family-action-icon"
                onClick={() => navigate("edit-member/" + member.id)}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open documents">
              <IconButton
                aria-label="Open family member documents"
                className="family-action-icon"
                onClick={() => navigate("member-documents/" + member.id)}
              >
                <FolderOpenOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}
